// CNV Work desktop (Electron) bridge. Khi web admin chạy trong cnvwork-desktop
// shell, preload script inject `window.cnvDesktop` để bypass các limitation:
//
//   - Web Push API (pushManager.subscribe) KHÔNG hoạt động trong Electron
//     (Chromium push service unavailable) → bỏ qua web push, dùng socket-only.
//   - Notification API native macOS cần đi qua main process để có icon đúng,
//     click handler, persistent notification center entry.
//
// Pattern này giống Slack/Discord/Linear: cùng codebase web, detect Electron
// shell, route notification calls qua IPC bridge thay vì browser API.

const bridge =
  typeof window !== 'undefined' && window.cnvDesktop && window.cnvDesktop.isDesktop === true
    ? window.cnvDesktop
    : null;

/** True khi đang chạy trong CNV Work desktop shell. */
export const isDesktopApp = !!bridge;

/** Platform string ('darwin' | 'win32' | 'linux') trong desktop, null nếu là browser. */
export const desktopPlatform = bridge?.platform ?? null;

/**
 * Show native OS notification.
 *
 * - Desktop: route qua IPC → main process tạo native Notification (macOS
 *   Notification Center / Windows toast / Linux libnotify). Persistent dù tab
 *   ẩn / app minimize. KHÔNG cần Web Push.
 * - Browser: fallback `new Notification(...)` nếu permission granted. Caller
 *   tự handle case permission default/denied.
 *
 * @param {{title: string, body: string, icon?: string, tag?: string, data?: any, navUrl?: string, onClick?: () => void}} opts
 * @returns {Promise<'desktop' | 'browser' | 'skip'>} method đã dùng để fire.
 */
export async function showNotification(opts) {
  const { title, body } = opts;
  if (!title) return 'skip';

  if (bridge) {
    try {
      // navUrl: route in-app để click noti điều hướng tới đúng tin nhắn.
      // Lấy từ opts.navUrl hoặc opts.data.url (các component noti set data.url).
      const navUrl = opts.navUrl || opts.data?.url || undefined;
      // icon: avatar người gửi → thay logo CNV Work (main fetch URL http(s)).
      // Bỏ qua icon relative (vd '/favicon.png') ở main → dùng app icon.
      await bridge.showNotification({ title, body, navUrl, icon: opts.icon });
      return 'desktop';
    } catch (e) {
      console.warn('[cnvDesktop] showNotification bridge failed:', e?.message);
      return 'skip';
    }
  }

  // Browser fallback. Caller phải tự ensure permission granted nếu muốn show.
  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    try {
      const n = new Notification(title, {
        body,
        icon: opts.icon,
        tag: opts.tag,
        data: opts.data,
      });
      if (opts.onClick) n.onclick = opts.onClick;
      return 'browser';
    } catch (e) {
      console.warn('[cnvDesktop] browser Notification failed:', e?.message);
      return 'skip';
    }
  }
  return 'skip';
}

/**
 * Set unread badge count trên app icon (Dock macOS / Taskbar Windows).
 * Browser: no-op (browser tab ko có badge API ngoài Web App Manifest setAppBadge,
 * và chỉ work khi installed PWA — bỏ qua cho đơn giản).
 *
 * @param {number} count Số unread tổng. 0 = clear badge.
 */
export async function setBadge(count) {
  const n = Math.max(0, Math.floor(count || 0));
  if (bridge) {
    try {
      await bridge.setBadge(n);
    } catch (e) {
      console.warn('[cnvDesktop] setBadge failed:', e?.message);
    }
  }
  // Browser fallback: navigator.setAppBadge nếu có (PWA installed)
  else if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
    try {
      if (n > 0) await navigator.setAppBadge(n);
      else await navigator.clearAppBadge?.();
    } catch {}
  }
}

/**
 * Đăng ký handler click native noti (desktop) → navigate tới navUrl.
 * Mount 1 LẦN ở app root (có router). Trả unsubscribe. No-op trong browser.
 *
 * @param {(navUrl: string) => void} cb
 * @returns {() => void} unsubscribe
 */
export function onDesktopNotificationClick(cb) {
  if (!bridge || typeof bridge.onNotificationClick !== 'function') return () => {};
  return bridge.onNotificationClick(cb);
}

/**
 * Đăng ký nhịp "đánh thức" từ desktop shell (main process bơm xuống).
 *
 * VÌ SAO CẦN: trong CNV Work desktop, bấm X trên macOS chỉ ẨN cửa sổ, app sống
 * tiếp ở tray. Cửa sổ ẩn → Chromium throttle timer của webview → heartbeat STOMP
 * (10s) và watchdog `setInterval` (30s) đều chết → WebSocket chat đứt mà không ai
 * vớt → tin nhắn mới KHÔNG có notification cho tới khi user mở lại cửa sổ.
 * (Đo thực tế 2026-08-13 trên máy sếp: im 16 phút rồi nổ dồn 36 noti một lúc do
 * catch-up replay sau reconnect.)
 *
 * IPC từ main KHÔNG phải timer nên không bị throttle → shell bơm nhịp 30s/lần +
 * các mốc window show/focus/restore, máy thức dậy, mở khoá màn hình. Consumer
 * (chatStomp) dùng nhịp này làm watchdog thay cho setInterval.
 *
 * @param {(info: {reason: string, ts: number}) => void} cb
 * @returns {() => void} unsubscribe (no-op trong browser)
 */
export function onDesktopWake(cb) {
  if (!bridge || typeof bridge.onDesktopWake !== 'function') return () => {};
  return bridge.onDesktopWake(cb);
}

/**
 * Open URL ngoài (external browser) thay vì navigate trong shell webview.
 * Desktop: bridge ra default browser. Browser: window.open new tab.
 */
export function openExternal(url) {
  if (!url) return;
  if (bridge && typeof bridge.openExternal === 'function') {
    bridge.openExternal(url).catch?.(() => {});
    return;
  }
  if (typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
